import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  carryProductsListNavState,
  productsListPath,
  resolveProductsListSearch,
} from '@/lib/productsListNavigation';
import { TableLink } from '@/components/global';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { MarginBadge, StatusBadge, ProductStatusBadge, MarginCalculator } from '@/components/products';
import { ArrowLeft, Package, Pencil, Trash2 } from 'lucide-react';

interface Category {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
  code: string;
}

interface ProductPrice {
  id: string;
  priceType: 'PURCHASE' | 'SELL';
  price: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  supplier?: Supplier | null;
  notes: string | null;
}

interface ProductMediaItem {
  id: string;
  url: string;
  mediaType: string;
  sortOrder: number;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  barcode: string | null;
  categoryId: string | null;
  supplierId: string | null;
  purchasePrice: number;
  sellPrice: number;
  targetMarginPercent: number;
  reorderLeadDays: number;
  isActive: boolean;
  status?: 'ACTIVE' | 'DORMANT' | 'DELISTED' | 'UNMANAGED';
  imageUrl: string | null;
  media?: ProductMediaItem[];
  category?: Category | null;
  supplier?: Supplier | null;
  marginPercent?: number;
  ragStatus?: 'green' | 'amber' | 'red';
  priceHistory?: ProductPrice[];
}

function resolveProductImageUrl(product: Product): string | null {
  const primary = product.imageUrl?.trim();
  if (primary) return primary;
  const images = product.media?.filter((m) => m.mediaType === 'IMAGE') ?? product.media ?? [];
  const first = images[0];
  return first?.url?.trim() || null;
}

function formatCurrency(value: number): string {
  return `£${Number(value).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const productsListHref = productsListPath(resolveProductsListSearch(location));
  const productsListNav = carryProductsListNavState(location);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/products/${id}?includePriceHistory=true`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Product not found');
          return;
        }
        throw new Error('Failed to load product');
      }
      const data = await res.json();
      setProduct(data);
    } catch (err: any) {
      setError(err.message || 'Error loading product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  async function handleDelete() {
    if (!product) return;
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/v1/products/${product.id}`, { method: 'DELETE' });
      if (res.ok) {
        navigate(productsListHref);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  }

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="py-8 text-center">
        <div className="text-red-600 mb-4">{error || 'Product not found'}</div>
        <Button variant="outline" onClick={() => navigate(productsListHref)}>
          Back to Products
        </Button>
      </div>
    );
  }

  const imageUrl = resolveProductImageUrl(product);

  return (
    <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(productsListHref)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {product.status === 'UNMANAGED' ? (
          <div
            className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100"
            role="status"
          >
            <p className="font-medium">This product is unmanaged in Express</p>
            <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
              It is hidden from lists, stock, and pickers. Cloud still syncs it. Set a Cloud
              status (Active, Dormant, or Delisted) on Edit to bring it back.
            </p>
          </div>
        ) : null}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">{product.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-muted-foreground">
              <span className="font-mono">{product.sku}</span>
              {product.barcode && (
                <>
                  <span>•</span>
                  <span>{product.barcode}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/inventory/products/${id}/edit`, productsListNav)}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Status</div>
            <div className="mt-1">
              {product.status ? (
                <ProductStatusBadge status={product.status} />
              ) : (
                <StatusBadge isActive={product.isActive} />
              )}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Margin</div>
            <div className="mt-1 min-h-[28px] flex items-center">
              {typeof product.marginPercent === 'number' && Number.isFinite(product.marginPercent) ? (
                <MarginBadge
                  marginPercent={product.marginPercent}
                  targetMargin={product.targetMarginPercent}
                />
              ) : (
                <span className="text-sm text-muted-foreground">
                  {Number(product.sellPrice) > 0 ? 'N/A' : 'No sell price'}
                </span>
              )}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Target Margin</div>
            <div className="text-2xl font-bold">{product.targetMarginPercent}%</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Reorder Lead</div>
            <div className="text-2xl font-bold">{product.reorderLeadDays} days</div>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Product Info</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-6 sm:flex-row sm:items-stretch">
              <div className="flex shrink-0 self-stretch justify-center sm:justify-start">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    loading="lazy"
                    className="mx-auto h-40 w-40 rounded-lg object-contain sm:mx-0 sm:h-full sm:w-auto sm:max-w-[10rem]"
                  />
                ) : (
                  <div
                    className="flex h-full min-h-[10rem] w-[10rem] items-center justify-center rounded-lg border border-dashed"
                    aria-hidden
                  >
                    <Package className="h-14 w-14 text-muted-foreground/45" />
                  </div>
                )}
              </div>
              <dl className="grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <dt className="text-muted-foreground">SKU</dt>
                <dd className="font-mono">{product.sku}</dd>

                <dt className="text-muted-foreground">Barcode</dt>
                <dd>{product.barcode || '-'}</dd>

                <dt className="text-muted-foreground">Category</dt>
                <dd>
                  {product.category ? (
                    <Badge variant="secondary">{product.category.name}</Badge>
                  ) : (
                    '-'
                  )}
                </dd>

                <dt className="text-muted-foreground">Supplier</dt>
                <dd>
                  {product.supplier ? (
                    <TableLink to={`/inventory/suppliers/${product.supplierId}`}>
                      {product.supplier.name}
                    </TableLink>
                  ) : (
                    '-'
                  )}
                </dd>

                <dt className="text-muted-foreground">Purchase Price</dt>
                <dd className="font-medium">
                  {Number(product.purchasePrice) > 0 ? (
                    formatCurrency(product.purchasePrice)
                  ) : (
                    <span className="text-muted-foreground font-normal">N/A</span>
                  )}
                </dd>

                <dt className="text-muted-foreground">Sell Price</dt>
                <dd className="font-medium">{formatCurrency(product.sellPrice)}</dd>
              </dl>
            </CardContent>
          </Card>

          <MarginCalculator
            purchasePrice={Number(product.purchasePrice)}
            sellPrice={Number(product.sellPrice)}
            targetMarginPercent={Number(product.targetMarginPercent)}
            supplierName={product.supplier?.name}
          />
        </div>

        {product.priceHistory && product.priceHistory.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Price History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Effective From</TableHead>
                    <TableHead>Effective To</TableHead>
                    <TableHead>Supplier</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.priceHistory.map((price) => (
                    <TableRow key={price.id}>
                      <TableCell>
                        <Badge variant={price.priceType === 'PURCHASE' ? 'secondary' : 'default'}>
                          {price.priceType}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(price.price)}</TableCell>
                      <TableCell>{formatDate(price.effectiveFrom)}</TableCell>
                      <TableCell>
                        {price.effectiveTo ? formatDate(price.effectiveTo) : 'Current'}
                      </TableCell>
                      <TableCell>{price.supplier?.name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
